/**
 * Jigsaw piece outlines — pure SVG path math (no DOM, no React).
 *
 * Every shared edge is drawn from one canonical curve (left→right for
 * horizontal edges, top→bottom for vertical ones, bulging toward the lower /
 * right neighbour when edge.s = +1). Both neighbours trace that same curve, one
 * of them in reverse, so tab and socket match exactly.
 *
 * Sizes are in pixels; `size` is the piece's shorter side, so knobs keep the
 * same proportions on any edge length. The deepest knob point is
 * 2.5 * TAB * k ≤ 0.23 of size, inside the engine's TAB_OVERHANG.
 */

const TAB = 0.088;

/**
 * Canonical cubic segments for one edge of `length` px: a list of
 * [p0, c1, c2, p3] points as [along, across] (across > 0 toward the lower /
 * right neighbour).
 */
export function edgeCurve(edge, length, size) {
  const c = edge.o * length;
  const k = edge.k * edge.s;
  const d = edge.d;
  const t = TAB;
  const p = (u, v) => [c + u * size, v * size * k];
  const p0 = [0, 0];
  const p1 = [c - 0.3 * size, 0];
  const p2 = p(d, -t);
  const p3 = p(-t, t);
  const p4 = p(-2 * t - d, 3 * t);
  const p5 = p(2 * t - d, 3 * t);
  const p6 = p(t, t);
  const p7 = p(d, -t);
  const p8 = [c + 0.3 * size, 0];
  const p9 = [length, 0];
  return [
    [p0, p1, p2, p3],
    [p3, p4, p5, p6],
    [p6, p7, p8, p9],
  ];
}

function reverseSegments(segments) {
  return segments
    .slice()
    .reverse()
    .map((seg) => [seg[3], seg[2], seg[1], seg[0]]);
}

/**
 * Outline of one piece, clockwise from its top-left corner, in local pixels
 * where the logical cell is the rectangle (inset, inset)–(inset + width,
 * inset + height). `sides` comes from the engine's pieceEdges(). Each side is
 * a list of cubic segments (a flat side is one straight segment).
 */
export function pieceSegments(sides, width, height, inset) {
  const size = Math.min(width, height);
  const x0 = inset;
  const y0 = inset;
  const x1 = inset + width;
  const y1 = inset + height;
  const build = (side, length, map, reverse, from, to) => {
    if (!side) return [[from, from, to, to]];
    const segs = edgeCurve(side.edge, length, size).map((seg) =>
      seg.map((pt) => map(pt[0], pt[1]))
    );
    return reverse ? reverseSegments(segs) : segs;
  };
  return {
    top: build(sides.top, width, (u, v) => [x0 + u, y0 + v], false, [x0, y0], [x1, y0]),
    right: build(sides.right, height, (u, v) => [x1 + v, y0 + u], false, [x1, y0], [x1, y1]),
    bottom: build(sides.bottom, width, (u, v) => [x0 + u, y1 + v], true, [x1, y1], [x0, y1]),
    left: build(sides.left, height, (u, v) => [x0 + v, y0 + u], true, [x0, y1], [x0, y0]),
  };
}

function num(value) {
  return String(Math.round(value * 100) / 100);
}

/** SVG path data for one piece (see pieceSegments for the coordinate frame). */
export function piecePath(sides, width, height, inset) {
  const outline = pieceSegments(sides, width, height, inset);
  let d = "M" + num(inset) + " " + num(inset);
  ["top", "right", "bottom", "left"].forEach((name) => {
    outline[name].forEach((seg) => {
      if (seg[0] === seg[1] && seg[2] === seg[3]) {
        d += "L" + num(seg[3][0]) + " " + num(seg[3][1]);
      } else {
        d +=
          "C" +
          [seg[1], seg[2], seg[3]].map((pt) => num(pt[0]) + " " + num(pt[1])).join(" ");
      }
    });
  });
  return d + "Z";
}
